import React from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export interface VideoVerticalPlayerSideControls {
  label?: React.ReactNode;
  icon: LucideIcon | false;
  fill?: boolean;
  /** Pressed state of a toggle (like, bookmark); leave unset on a plain action. */
  active?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  testId?: string;
}
interface PlayerSideControlProps extends VideoVerticalPlayerSideControls {
  index?: number;
  sideControlsLength?: number;
  isDisplayed: boolean;
  className?: string;
  direction?: "right" | "left" | "bottom" | "top" | "none";
}

type PlayerSideControl = React.FC<PlayerSideControlProps> & {
  Skeleton: React.FC<{
    label?: string;
    icon: LucideIcon | false;
    className?: string;
  }>;
};

const Skeleton: React.FC<{
  label?: string;
  icon: LucideIcon | false;
  className?: string;
}> = ({ label, icon: Icon, className }) => (
  <div
    className={cn(
      "flex animate-pulse flex-col items-center gap-2 text-neutral-50",
      className,
    )}
  >
    {Icon && <div className="size-8 rounded bg-current" />}
    {label && <div className="h-2 w-6 rounded bg-current" />}
  </div>
);

const PlayerSideControl: PlayerSideControl = ({
  index = 0,
  sideControlsLength = 0,
  isDisplayed,
  label,
  icon: Icon,
  fill = false,
  active,
  className,
  direction = "right",
  onClick,
  testId,
}) => (
  <button
    data-test-id={testId}
    aria-pressed={active}
    className={cn(
      "text-caption hover:text-neutral-30 active:text-neutral-40 flex size-16 translate-0 cursor-pointer flex-col items-center justify-center gap-2 text-white transition-all duration-300",
      !isDisplayed && direction === "right" && "translate-x-10 opacity-0",
      !isDisplayed && direction === "left" && "-translate-x-10 opacity-0",
      !isDisplayed && direction === "top" && "translate-y-10 opacity-0",
      !isDisplayed && direction === "bottom" && "-translate-y-10 opacity-0",
      !isDisplayed && direction === "none" && "opacity-0",
      className,
    )}
    style={{
      transitionDelay: `${index * (300 / sideControlsLength)}ms`,
    }}
    onClick={onClick}
  >
    {Icon && (
      <Icon
        className={cn(
          "shrink-0 md:size-6",
          fill && "fill-current",
          active && "text-brand-40",
        )}
      />
    )}
    {label && <span>{label}</span>}
  </button>
);

PlayerSideControl.Skeleton = Skeleton;

export default PlayerSideControl;
